import { useState } from 'react';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { deleteStorageTable, newStorageRequestId } from '../../services/azureStorageApi';
import { trackStorageEvent } from '../../services/storageAnalytics';
import { tableAdminFailureText } from './tableAdminFailure';
import { getConnectionById } from '../../../../connections/utils/connectionLookup';
import { recordTableAdminOutcome } from '../model/tableAdminRefusals';

type Props = {
  open: boolean;
  connectionId: string;
  table: string;
  onOpenChange: (open: boolean) => void;
  readOnlyReason?: string;
  onDelete?: (table: string) => Promise<unknown> | unknown;
  onDeleted?: () => void;
};

/**
 * Delete-table confirmation on S2's shared `ConfirmDialog` (review C C8): the
 * typed-name gate is `requireTypedName`, whose `label` S2 renders verbatim, so
 * the label and the description name the table themselves. S2's dialog has no
 * disabled-with-reason state, so a read-only connection never reaches a confirm
 * action: it gets the reason and a Close button instead. A refused delete keeps
 * the dialog open (S2's retry contract) and shows the typed IPC reason inside the
 * description, S2's dialog having `onError` but no error slot (S4 review F, F9).
 */
export function DeleteStorageTableDialog({ open, connectionId, table, onOpenChange, readOnlyReason, onDelete, onDeleted }: Props) {
  const [failure, setFailure] = useState<string>();
  if (readOnlyReason) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="storage-table-delete-refused">
          <DialogHeader>
            <DialogTitle>Delete table</DialogTitle>
            <DialogDescription>{readOnlyReason}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  const remove = async (): Promise<void> => {
    setFailure(undefined);
    trackStorageEvent('feature:storage_table_admin', { action: 'delete', outcome: 'attempt' });
    try {
      if (onDelete) await onDelete(table);
      else await deleteStorageTable({ connectionId, table, requestId: newStorageRequestId(), timeoutMs: 30_000 });
      trackStorageEvent('feature:storage_table_admin', { action: 'delete', outcome: 'success' });
      const connection = getConnectionById(connectionId).connection;
      if (connection) recordTableAdminOutcome(connection, { op: 'delete', table });
      onDeleted?.();
    } catch (error) {
      trackStorageEvent('feature:storage_table_admin', { action: 'delete', outcome: 'failure' });
      // C2 R10: a permission refusal (tables/delete) is remembered for the menus — for this table only (C2-01).
      const connection = getConnectionById(connectionId).connection;
      if (connection) recordTableAdminOutcome(connection, { op: 'delete', table }, error);
      throw error;
    }
  };
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => { if (!next) setFailure(undefined); onOpenChange(next); }}
      title="Delete table"
      description={<>
        {`This permanently deletes the table ${table} and every entity it contains. This action cannot be undone.`}
        {failure && <span role="alert" className="mt-2 block text-destructive">{failure}</span>}
      </>}
      confirmLabel="Delete table"
      variant="destructive"
      requireTypedName={{ value: table, label: `Type the table name ${table} to confirm` }}
      onConfirm={remove}
      onError={(error) => setFailure(tableAdminFailureText('deleted', error))}
    />
  );
}
